import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import { ArrowUpTrayIcon, TrashIcon } from "@heroicons/react/24/outline";
import { createApolloClient } from "../../providers/ApolloClientFactory";
import { useEventStore } from "../../store/event";
import UploadUIDsModal from "../rfid_assets/UploadUIDsModal";
import {
  GET_UID_SUMMARY_ROWS,
  GET_UID_EVENT_AGGREGATES,
  GET_UID_TOKEN_ROWS,
  CHECK_UIDS,
  DELETE_UIDS,
} from "./queries";

const client = createApolloClient();

// Hasura handles _in lists of this size comfortably; chunking keeps any single
// request (and any single delete transaction) bounded.
const CHECK_CHUNK = 1000;
const DELETE_CHUNK = 500;

// ===== Types =====
type SummaryRow = {
  uid: string;
  access_type: string | null;
  is_active: boolean;
  is_qr: boolean | null;
  is_access_only: boolean | null;
  attendee_id: number | null;
  reg_id: string | null;
  cash_balance: number | null; // cents
  promo_balance: number | null; // cents
};

type CountAgg = { aggregate: { count: number } | null };
type BalanceAgg = {
  aggregate: {
    count: number;
    sum: { cash_balance: number | null; promo_balance: number | null } | null;
  } | null;
};

type AggregatesData = {
  total: {
    aggregate: {
      count: number;
      sum: { cash_balance: number | null; promo_balance: number | null } | null;
      min: { created_at: string | null } | null;
      max: { updated_at: string | null } | null;
    } | null;
  };
  active: CountAgg;
  assigned: CountAgg;
  registered: CountAgg;
  qr: CountAgg;
  accessOnly: CountAgg;
  funded: CountAgg;
  withTokens: CountAgg;
  phoneLinked: CountAgg;
  withPin: CountAgg;
  revokedWithBalance: BalanceAgg;
  accessOnlyWithBalance: BalanceAgg;
  noAccessType: CountAgg;
  cashLoads: {
    aggregate: { count: number; sum: { added_cash: number | null } | null } | null;
  };
  promoLoads: {
    aggregate: { count: number; sum: { added_promo: number | null } | null } | null;
  };
  transfers: { status: string | null }[];
};

// Aggregates reduced to plain numbers for rendering.
type EventStats = {
  total: number;
  cashSum: number;
  promoSum: number;
  firstCreated: string | null;
  lastUpdated: string | null;
  active: number;
  assigned: number;
  registered: number;
  qr: number;
  accessOnly: number;
  funded: number;
  withTokens: number;
  phoneLinked: number;
  withPin: number;
  revokedWithBalance: { count: number; amount: number };
  accessOnlyWithBalance: { count: number; amount: number };
  noAccessType: number;
  cashLoads: { count: number; amount: number };
  promoLoads: { count: number; amount: number };
  transfers: { total: number; completed: number; reversed: number };
};

const reduceStats = (d: AggregatesData): EventStats => {
  const count = (a: CountAgg) => a.aggregate?.count ?? 0;
  const balance = (a: BalanceAgg) => ({
    count: a.aggregate?.count ?? 0,
    amount:
      (a.aggregate?.sum?.cash_balance ?? 0) +
      (a.aggregate?.sum?.promo_balance ?? 0),
  });
  return {
    total: d.total.aggregate?.count ?? 0,
    cashSum: d.total.aggregate?.sum?.cash_balance ?? 0,
    promoSum: d.total.aggregate?.sum?.promo_balance ?? 0,
    firstCreated: d.total.aggregate?.min?.created_at ?? null,
    lastUpdated: d.total.aggregate?.max?.updated_at ?? null,
    active: count(d.active),
    assigned: count(d.assigned),
    registered: count(d.registered),
    qr: count(d.qr),
    accessOnly: count(d.accessOnly),
    funded: count(d.funded),
    withTokens: count(d.withTokens),
    phoneLinked: count(d.phoneLinked),
    withPin: count(d.withPin),
    revokedWithBalance: balance(d.revokedWithBalance),
    accessOnlyWithBalance: balance(d.accessOnlyWithBalance),
    noAccessType: count(d.noAccessType),
    cashLoads: {
      count: d.cashLoads.aggregate?.count ?? 0,
      amount: d.cashLoads.aggregate?.sum?.added_cash ?? 0,
    },
    promoLoads: {
      count: d.promoLoads.aggregate?.count ?? 0,
      amount: d.promoLoads.aggregate?.sum?.added_promo ?? 0,
    },
    transfers: {
      total: d.transfers.length,
      completed: d.transfers.filter((t) => t.status === "Completed").length,
      reversed: d.transfers.filter((t) => t.status === "Reversed").length,
    },
  };
};

// tokens_balance is a jsonb map of { redeemable_token_name, balance } entries
// (sometimes nested one level under a tokens_balance key — same unwrapping the
// transfer-history grid does).
const reduceTokenTotals = (rows: { tokens_balance: any }[]) => {
  const byName = new Map<string, { total: number; holders: number }>();
  let holders = 0;
  let totalTokens = 0;
  for (const row of rows) {
    const raw = row.tokens_balance;
    const tokens = raw && typeof raw === "object" ? raw.tokens_balance ?? raw : null;
    if (!tokens || typeof tokens !== "object") continue;
    let held = false;
    for (const t of Object.values<any>(tokens)) {
      const name = t?.redeemable_token_name;
      const bal = Number(t?.balance) || 0;
      if (!name || bal <= 0) continue;
      held = true;
      totalTokens += bal;
      const entry = byName.get(name) ?? { total: 0, holders: 0 };
      entry.total += bal;
      entry.holders += 1;
      byName.set(name, entry);
    }
    if (held) holders++;
  }
  return {
    holders,
    totalTokens,
    byName: Array.from(byName.entries())
      .map(([name, v]) => ({ name, ...v }))
      .sort((a, b) => b.total - a.total),
  };
};

type CheckedAsset = {
  id: number;
  uid: string;
  access_type: string | null;
  reg_id: string | null;
  is_active: boolean;
  attendee_id: number | null;
  cash_balance: number | null;
  promo_balance: number | null;
};

type TypeBreakdown = {
  type: string;
  total: number;
  active: number;
  assigned: number;
  registered: number;
  qr: number;
  accessOnly: number;
  cash: number;
  promo: number;
};

// ===== Formatters =====
const fmtCents = (cents: number) =>
  `$${(cents / 100).toLocaleString("en-US", { minimumFractionDigits: 2 })}`;
const n = (v: number) => v.toLocaleString("en-US");
const fmtDate = (iso: string) =>
  new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
const fmtDay = (d: Date) =>
  d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

const chunk = <T,>(list: T[], size: number): T[][] => {
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += size) out.push(list.slice(i, i + size));
  return out;
};

const parseUidList = (raw: string): string[] => {
  // One per line; tolerate the commas/tabs that come along with Excel pastes.
  const seen = new Set<string>();
  return raw
    .split(/[\n,\t]/)
    .map((s) => s.trim())
    .filter((s) => s !== "")
    .filter((s) => (seen.has(s) ? false : (seen.add(s), true)));
};

const Stat = ({ label, value, sub }: { label: string; value: string; sub?: string }) => (
  <div className="flex flex-col rounded-lg border border-gray-200 bg-gray-50/60 px-4 py-3">
    <span className="text-xs font-medium uppercase tracking-wide text-gray-500">
      {label}
    </span>
    <span className="text-xl font-bold tabular-nums text-gray-900">{value}</span>
    {sub && <span className="text-xs text-gray-500">{sub}</span>}
  </div>
);

// ===== Page =====
export const UidManagerPage = () => {
  const headerEventId = useEventStore((s) => s.eventId);
  const allEvents = useEventStore((s) => s.allEvents);
  const eventId = headerEventId ? Number(headerEventId) : null;
  const selectedEvent = useMemo(
    () => (allEvents ?? []).find((e: any) => Number(e?.id) === eventId) ?? null,
    [allEvents, eventId]
  );
  const eventName = selectedEvent?.name ?? null;

  // Whether the event is over decides how outstanding balances read: still in
  // play, or final leftovers (refund liability / breakage).
  const eventTiming = useMemo(() => {
    const parse = (v: unknown) => {
      const d = v ? new Date(v as string) : null;
      return d && !isNaN(d.getTime()) ? d : null;
    };
    const start = parse(selectedEvent?.start_date);
    const end = parse(selectedEvent?.end_date);
    if (!end) return null;
    const now = Date.now();
    // Dates are date-only strings, so end_date parses to the START of that
    // day — the event runs through the end of it. Only call the event over
    // once its final day has fully passed.
    if (end.getTime() + 86_400_000 < now) {
      return {
        state: "ended" as const,
        end,
        daysSince: Math.floor((now - end.getTime()) / 86_400_000),
      };
    }
    if (start && start.getTime() > now) return { state: "upcoming" as const, end };
    return { state: "live" as const, end };
  }, [selectedEvent]);

  // ----- Summary -----
  const [rows, setRows] = useState<SummaryRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<EventStats | null>(null);
  const [tokenTotals, setTokenTotals] = useState<ReturnType<typeof reduceTokenTotals> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadSummary = useCallback(async () => {
    if (!eventId) {
      setRows([]);
      setTotalCount(0);
      setStats(null);
      setTokenTotals(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [rowsRes, aggRes, tokenRes] = await Promise.all([
        client.query<{
          rfid_assets: SummaryRow[];
          rfid_assets_aggregate: { aggregate: { count: number } | null };
        }>({
          query: GET_UID_SUMMARY_ROWS,
          variables: { eventId },
          fetchPolicy: "network-only",
        }),
        client.query<AggregatesData>({
          query: GET_UID_EVENT_AGGREGATES,
          variables: { eventId },
          fetchPolicy: "network-only",
        }),
        client.query<{ rfid_assets: { tokens_balance: any }[] }>({
          query: GET_UID_TOKEN_ROWS,
          variables: { eventId },
          fetchPolicy: "network-only",
        }),
      ]);
      setRows(rowsRes.data.rfid_assets);
      setTotalCount(rowsRes.data.rfid_assets_aggregate.aggregate?.count ?? 0);
      setStats(reduceStats(aggRes.data));
      setTokenTotals(reduceTokenTotals(tokenRes.data.rfid_assets));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load UID summary");
      setRows([]);
      setTotalCount(0);
      setStats(null);
      setTokenTotals(null);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    loadSummary();
  }, [loadSummary]);

  const byType = useMemo<TypeBreakdown[]>(() => {
    const map = new Map<string, TypeBreakdown>();
    for (const r of rows) {
      const type = r.access_type?.trim() || "(no access type)";
      const entry =
        map.get(type) ??
        {
          type,
          total: 0,
          active: 0,
          assigned: 0,
          registered: 0,
          qr: 0,
          accessOnly: 0,
          cash: 0,
          promo: 0,
        };
      entry.total++;
      if (r.is_active) entry.active++;
      if (r.attendee_id != null) entry.assigned++;
      if (r.reg_id && r.reg_id.trim() !== "") entry.registered++;
      if (r.is_qr) entry.qr++;
      if (r.is_access_only) entry.accessOnly++;
      entry.cash += r.cash_balance ?? 0;
      entry.promo += r.promo_balance ?? 0;
      map.set(type, entry);
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [rows]);

  // Duplicate UID values inside this event — a classic symptom of double
  // uploads. Computed from the (possibly capped) row pull.
  const dupes = useMemo(() => {
    const seen = new Map<string, number>();
    for (const r of rows) seen.set(r.uid, (seen.get(r.uid) ?? 0) + 1);
    let uids = 0, extraRows = 0;
    for (const c of seen.values()) {
      if (c > 1) {
        uids++;
        extraRows += c - 1;
      }
    }
    return { uids, extraRows };
  }, [rows]);

  // ----- Bulk upload (reuses the existing modal) -----
  const [uploadOpen, setUploadOpen] = useState(false);
  const closeUpload = () => {
    setUploadOpen(false);
    loadSummary();
  };

  // ----- Bulk delete -----
  const [deleteText, setDeleteText] = useState("");
  const [checking, setChecking] = useState(false);
  const [checked, setChecked] = useState<{
    matches: CheckedAsset[];
    missing: string[];
  } | null>(null);
  const [ackRisky, setAckRisky] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteProgress, setDeleteProgress] = useState<{ done: number; total: number } | null>(null);

  const pastedUids = useMemo(() => parseUidList(deleteText), [deleteText]);

  // Any edit to the list invalidates the previous dry-run.
  useEffect(() => {
    setChecked(null);
    setAckRisky(false);
  }, [deleteText, eventId]);

  const runCheck = async () => {
    if (!eventId || pastedUids.length === 0) return;
    setChecking(true);
    try {
      const matches: CheckedAsset[] = [];
      for (const part of chunk(pastedUids, CHECK_CHUNK)) {
        const { data } = await client.query<{ rfid_assets: CheckedAsset[] }>({
          query: CHECK_UIDS,
          variables: { eventId, uids: part },
          fetchPolicy: "network-only",
        });
        matches.push(...data.rfid_assets);
      }
      const found = new Set(matches.map((m) => m.uid));
      setChecked({
        matches,
        missing: pastedUids.filter((u) => !found.has(u)),
      });
    } catch (e: any) {
      toast.error("Check failed: " + (e?.message ?? ""));
    } finally {
      setChecking(false);
    }
  };

  const risky = useMemo(() => {
    if (!checked) return { withBalance: 0, withAttendee: 0, activeCount: 0 };
    let withBalance = 0, withAttendee = 0, activeCount = 0;
    for (const m of checked.matches) {
      if ((m.cash_balance ?? 0) > 0 || (m.promo_balance ?? 0) > 0) withBalance++;
      if (m.attendee_id != null) withAttendee++;
      if (m.is_active) activeCount++;
    }
    return { withBalance, withAttendee, activeCount };
  }, [checked]);

  const hasRisk =
    risky.withBalance > 0 || risky.withAttendee > 0 || risky.activeCount > 0;

  // Snapshot-before-delete, same philosophy as the SQL runbooks: the matched
  // rows leave the database for good, so a local CSV backup downloads first.
  const downloadBackup = (matches: CheckedAsset[]) => {
    const header = "id,uid,access_type,reg_id,is_active,attendee_id,cash_balance_cents,promo_balance_cents";
    const lines = [header].concat(
      matches.map((m) =>
        [m.id, m.uid, m.access_type ?? "", m.reg_id ?? "", m.is_active, m.attendee_id ?? "", m.cash_balance ?? 0, m.promo_balance ?? 0]
          .map((c) => `"${String(c).replace(/"/g, '""')}"`)
          .join(",")
      )
    );
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `uid_delete_backup_event${eventId}_${new Date().toISOString().slice(0, 19).replace(/[:T]/g, "")}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const runDelete = async () => {
    if (!eventId || !checked || checked.matches.length === 0) return;
    if (hasRisk && !ackRisky) return;
    const confirmed = window.confirm(
      `Delete ${n(checked.matches.length)} UIDs from ${eventName ?? `event #${eventId}`}? ` +
        `A backup CSV will download first. This cannot be undone from the dashboard.`
    );
    if (!confirmed) return;

    downloadBackup(checked.matches);

    setDeleting(true);
    setDeleteProgress({ done: 0, total: checked.matches.length });
    let affected = 0;
    try {
      const uids = checked.matches.map((m) => m.uid);
      for (const part of chunk(uids, DELETE_CHUNK)) {
        const { data } = await client.mutate<{
          delete_rfid_assets: { affected_rows: number };
        }>({
          mutation: DELETE_UIDS,
          variables: { eventId, uids: part },
        });
        affected += data?.delete_rfid_assets?.affected_rows ?? 0;
        setDeleteProgress((p) =>
          p ? { ...p, done: Math.min(p.total, p.done + part.length) } : p
        );
      }
      toast.success(`Deleted ${n(affected)} UIDs`);
      setDeleteText("");
      setChecked(null);
      await loadSummary();
    } catch (e: any) {
      toast.error(
        `Delete stopped after ${n(affected)} rows: ` + (e?.message ?? "")
      );
      // Re-run the dry-run so the remaining state on screen is accurate.
      await runCheck();
      await loadSummary();
    } finally {
      setDeleting(false);
      setDeleteProgress(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      {/* Header + summary */}
      <div className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <div className="flex items-center justify-between gap-3">
          <div>
            <h1 className="text-xl font-bold text-gray-900">
              UID Manager
              {loading && (
                <span className="ml-2 text-xs font-normal text-gray-400">loading…</span>
              )}
            </h1>
            <p className="text-xs text-gray-500">
              {eventName
                ? `Cashless UIDs for ${eventName}`
                : "Select an organization and event in the header"}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/rfid_assets"
              className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50"
            >
              Open asset list
            </Link>
            <button
              onClick={() => setUploadOpen(true)}
              disabled={!eventId}
              title={eventId ? undefined : "Select an event in the header first"}
              className="inline-flex items-center gap-1.5 rounded-lg bg-red-700 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-800 disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              <ArrowUpTrayIcon className="h-4 w-4" />
              Bulk upload UIDs
            </button>
          </div>
        </div>

        {error && (
          <div className="mt-4 rounded-lg border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-800">
            {error}
          </div>
        )}

        {eventId && (
          <>
            <div className="mt-5 grid grid-cols-2 gap-3 md:grid-cols-6">
              <Stat label="Total UIDs" value={n(stats?.total ?? totalCount)} />
              <Stat
                label="Activated"
                value={n(stats?.active ?? 0)}
                sub="card-on-file linked"
              />
              <Stat
                label="Assigned"
                value={n(stats?.assigned ?? 0)}
                sub="linked to an attendee"
              />
              <Stat
                label="Registered"
                value={n(stats?.registered ?? 0)}
                sub="have a Print ID"
              />
              <Stat
                label="QR-based"
                value={n(stats?.qr ?? 0)}
                sub={`${n((stats?.total ?? 0) - (stats?.qr ?? 0))} physical RFID`}
              />
              <Stat
                label="Access-only"
                value={n(stats?.accessOnly ?? 0)}
                sub="no wallet"
              />
            </div>
            {stats && (
              <div className="mt-3 flex flex-wrap items-baseline justify-between gap-2 text-xs">
                <span className="text-gray-500">
                  {n(stats.funded)} holding a balance · {n(stats.withTokens)}{" "}
                  holding tokens · {n(stats.phoneLinked)} phone-linked ·{" "}
                  {n(stats.withPin)} with a personal PIN
                </span>
                {stats.firstCreated && (
                  <span className="text-gray-400">
                    First upload {fmtDate(stats.firstCreated)}
                    {stats.lastUpdated && (
                      <> · Last activity {fmtDate(stats.lastUpdated)}</>
                    )}
                  </span>
                )}
              </div>
            )}
          </>
        )}
      </div>

      {eventId && (
        <>
          {/* Wallet liability & money movement */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-1 text-base font-semibold text-gray-900">Money on UIDs</h2>
            <p className="mb-3 text-xs text-gray-500">
              Left three: what is still sitting on wristbands right now. Right
              three: how money moved onto and between them.
              {eventTiming?.state === "live" &&
                " The event is live — these balances are still being loaded and spent."}
              {eventTiming?.state === "upcoming" &&
                " The event hasn't started — balances are pre-loads."}
            </p>
            {eventTiming?.state === "ended" &&
              stats &&
              (stats.cashSum > 0 ||
                stats.promoSum > 0 ||
                (tokenTotals?.totalTokens ?? 0) > 0) && (
                <div className="mb-3 rounded-md border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs text-indigo-900">
                  <span className="font-semibold">Final leftovers — </span>
                  {eventName ?? "this event"} ended {fmtDay(eventTiming.end)}
                  {eventTiming.daysSince === 0
                    ? " (today)"
                    : eventTiming.daysSince === 1
                      ? " (yesterday)"
                      : ` (${n(eventTiming.daysSince)} days ago)`}
                  , so these balances will never be spent:{" "}
                  {fmtCents(stats.cashSum + stats.promoSum)} across{" "}
                  {n(stats.funded)} UIDs ({fmtCents(stats.cashSum)} cash ·{" "}
                  {fmtCents(stats.promoSum)} promo)
                  {(tokenTotals?.totalTokens ?? 0) > 0 && (
                    <>
                      , plus {n(tokenTotals!.totalTokens)} unredeemed tokens
                    </>
                  )}
                  . Cash is a refund liability unless treated as breakage; promo
                  and tokens are event-scoped and can no longer be redeemed.
                </div>
              )}
            <div className="grid grid-cols-2 gap-3 md:grid-cols-6">
              <Stat
                label="Cash balance"
                value={fmtCents(stats?.cashSum ?? 0)}
                sub="outstanding"
              />
              <Stat
                label="Promo balance"
                value={fmtCents(stats?.promoSum ?? 0)}
                sub="outstanding"
              />
              <Stat
                label="Tokens"
                value={n(tokenTotals?.totalTokens ?? 0)}
                sub={`on ${n(tokenTotals?.holders ?? 0)} UIDs`}
              />
              <Stat
                label="Cash loaded"
                value={fmtCents(stats?.cashLoads.amount ?? 0)}
                sub={`${n(stats?.cashLoads.count ?? 0)} top-ups`}
              />
              <Stat
                label="Promo loaded"
                value={fmtCents(stats?.promoLoads.amount ?? 0)}
                sub={`${n(stats?.promoLoads.count ?? 0)} loads`}
              />
              <Stat
                label="Transfers"
                value={n(stats?.transfers.total ?? 0)}
                sub={
                  stats
                    ? `${n(stats.transfers.completed)} completed · ${n(stats.transfers.reversed)} reversed`
                    : undefined
                }
              />
            </div>
            {tokenTotals && tokenTotals.byName.length > 0 && (
              <div className="mt-5">
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">
                    Outstanding tokens
                  </h3>
                  <span className="text-xs text-gray-500">
                    {n(tokenTotals.totalTokens)} unredeemed on{" "}
                    {n(tokenTotals.holders)} UIDs
                  </span>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-gray-500">
                        <th className="px-3 py-1.5 font-medium">Token</th>
                        <th className="px-3 py-1.5 text-right font-medium">Outstanding</th>
                        <th className="px-3 py-1.5 text-right font-medium">UIDs holding</th>
                        <th className="w-1/3 px-3 py-1.5 font-medium">Share</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100">
                      {tokenTotals.byName.map((t) => {
                        const pct =
                          tokenTotals.totalTokens > 0
                            ? (t.total / tokenTotals.totalTokens) * 100
                            : 0;
                        return (
                          <tr key={t.name}>
                            <td className="px-3 py-1.5 font-medium text-gray-900">
                              {t.name}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums">
                              {n(t.total)}
                            </td>
                            <td className="px-3 py-1.5 text-right tabular-nums">
                              {n(t.holders)}
                            </td>
                            <td className="px-3 py-1.5">
                              <div className="flex items-center gap-2">
                                <div className="h-2 flex-1 rounded-full bg-gray-200">
                                  <div
                                    className="h-2 rounded-full bg-red-700"
                                    style={{ width: `${pct}%`, minWidth: 2 }}
                                  />
                                </div>
                                <span className="w-9 text-right tabular-nums text-gray-500">
                                  {pct.toFixed(0)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </section>

          {/* Hygiene flags */}
          {stats && (
            <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
              <h2 className="mb-3 text-base font-semibold text-gray-900">
                Needs attention
              </h2>
              {(() => {
                const flags = [
                  stats.revokedWithBalance.count > 0 && {
                    key: "revoked-balance",
                    text: `${n(stats.revokedWithBalance.count)} UIDs were deactivated after being linked to an attendee and still hold ${fmtCents(stats.revokedWithBalance.amount)} — likely revoked media whose balance never moved to a replacement.`,
                  },
                  stats.accessOnlyWithBalance.count > 0 && {
                    key: "access-only-balance",
                    text: `${n(stats.accessOnlyWithBalance.count)} access-only UIDs carry ${fmtCents(stats.accessOnlyWithBalance.amount)} — access-only assets shouldn't hold wallet money.`,
                  },
                  stats.noAccessType > 0 && {
                    key: "no-type",
                    text: `${n(stats.noAccessType)} UIDs have no access type, so they won't group under any ticket type.`,
                  },
                  dupes.uids > 0 && {
                    key: "dupes",
                    text: `${n(dupes.uids)} UID values appear more than once in this event (${n(dupes.extraRows)} extra rows)${totalCount > rows.length ? ` — checked the first ${n(rows.length)} of ${n(totalCount)} rows` : ""}.`,
                  },
                ].filter(Boolean) as { key: string; text: string }[];
                return flags.length > 0 ? (
                  <ul className="space-y-2">
                    {flags.map((f) => (
                      <li
                        key={f.key}
                        className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800"
                      >
                        {f.text}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="rounded-md border border-green-200 bg-green-50 px-3 py-2 text-xs text-green-800">
                    No hygiene issues found — no revoked media holding money,
                    no missing access types, no duplicate UIDs.
                  </p>
                );
              })()}
            </section>
          )}

          {/* Per-type breakdown */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-3 text-base font-semibold text-gray-900">
              By access type
              {!loading && totalCount > rows.length && (
                <span className="ml-2 text-xs font-normal text-amber-700">
                  based on first {n(rows.length)} of {n(totalCount)} rows
                </span>
              )}
            </h2>
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b border-gray-200 text-left text-gray-500">
                    <th className="px-3 py-1.5 font-medium">Access Type</th>
                    <th className="px-3 py-1.5 text-right font-medium">UIDs</th>
                    <th className="px-3 py-1.5 text-right font-medium">Activated</th>
                    <th className="px-3 py-1.5 text-right font-medium">Assigned</th>
                    <th className="px-3 py-1.5 text-right font-medium">Registered</th>
                    <th className="px-3 py-1.5 text-right font-medium">QR</th>
                    <th className="px-3 py-1.5 text-right font-medium">Access-only</th>
                    <th className="px-3 py-1.5 text-right font-medium">Cash</th>
                    <th className="px-3 py-1.5 text-right font-medium">Promo</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byType.map((t) => (
                    <tr key={t.type}>
                      <td className="px-3 py-1.5 font-medium text-gray-900">
                        {t.type === "(no access type)" ? (
                          <span className="font-normal text-gray-400">{t.type}</span>
                        ) : (
                          t.type
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(t.total)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(t.active)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(t.assigned)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(t.registered)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(t.qr)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{n(t.accessOnly)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtCents(t.cash)}</td>
                      <td className="px-3 py-1.5 text-right tabular-nums">{fmtCents(t.promo)}</td>
                    </tr>
                  ))}
                  {!loading && byType.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-3 py-6 text-center text-gray-400">
                        No UIDs uploaded for this event yet.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          {/* Bulk delete */}
          <section className="mb-4 rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
            <h2 className="mb-1 flex items-center gap-1.5 text-base font-semibold text-gray-900">
              <TrashIcon className="h-4 w-4 text-red-700" />
              Bulk delete
            </h2>
            <p className="mb-3 text-xs text-gray-500">
              Paste UIDs (one per line — Excel pastes work), check them against{" "}
              {eventName ?? "the event"}, review what would be removed, then
              delete. A backup CSV of the deleted rows downloads automatically.
            </p>
            <textarea
              value={deleteText}
              onChange={(e) => setDeleteText(e.target.value)}
              placeholder="Paste UIDs to delete, one per line"
              rows={6}
              disabled={deleting}
              className="block w-full rounded-md border-gray-300 font-mono text-sm shadow-sm focus:border-red-500 focus:ring-red-500 disabled:bg-gray-100"
            />
            <div className="mt-2 flex items-center justify-between">
              <span className="text-xs text-gray-500">
                {pastedUids.length > 0
                  ? `${n(pastedUids.length)} unique UIDs pasted`
                  : ""}
              </span>
              <button
                onClick={runCheck}
                disabled={pastedUids.length === 0 || checking || deleting}
                className="rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 shadow-sm transition-colors hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
              >
                {checking ? "Checking…" : "Check UIDs"}
              </button>
            </div>

            {checked && (
              <div className="mt-4 space-y-3">
                <div className="grid grid-cols-2 gap-3 md:grid-cols-5">
                  <Stat label="Will delete" value={n(checked.matches.length)} />
                  <Stat label="Not found" value={n(checked.missing.length)} sub="skipped" />
                  <Stat label="With balance" value={n(risky.withBalance)} />
                  <Stat label="Assigned" value={n(risky.withAttendee)} />
                  <Stat label="Active" value={n(risky.activeCount)} />
                </div>

                {checked.missing.length > 0 && (
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2 text-xs text-gray-600">
                    Not on this event (skipped):{" "}
                    <span className="font-mono">
                      {checked.missing.slice(0, 10).join(", ")}
                      {checked.missing.length > 10 &&
                        ` … +${n(checked.missing.length - 10)} more`}
                    </span>
                  </div>
                )}

                {hasRisk && (
                  <label className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-800">
                    <input
                      type="checkbox"
                      checked={ackRisky}
                      onChange={() => setAckRisky((v) => !v)}
                      className="mt-0.5 h-3.5 w-3.5 rounded border-gray-300 text-red-600 focus:ring-red-500"
                    />
                    <span>
                      I understand this includes{" "}
                      {[
                        risky.withBalance > 0 && `${n(risky.withBalance)} UIDs with money on them`,
                        risky.withAttendee > 0 && `${n(risky.withAttendee)} assigned to attendees`,
                        risky.activeCount > 0 && `${n(risky.activeCount)} active UIDs`,
                      ]
                        .filter(Boolean)
                        .join(", ")}{" "}
                      — and I still want to delete them.
                    </span>
                  </label>
                )}

                {deleteProgress && (
                  <div className="rounded-md border border-blue-200 bg-blue-50 p-3">
                    <div className="mb-1.5 text-xs text-blue-900">
                      Deleting {n(deleteProgress.done)} / {n(deleteProgress.total)}
                    </div>
                    <div className="h-2 w-full rounded-full bg-blue-200">
                      <div
                        className="h-2 rounded-full bg-blue-600 transition-all"
                        style={{ width: `${(deleteProgress.done / deleteProgress.total) * 100}%` }}
                      />
                    </div>
                  </div>
                )}

                <div className="flex justify-end">
                  <button
                    onClick={runDelete}
                    disabled={
                      checked.matches.length === 0 ||
                      deleting ||
                      (hasRisk && !ackRisky)
                    }
                    className="rounded-lg bg-red-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:bg-gray-300"
                  >
                    {deleting
                      ? "Deleting…"
                      : `Delete ${n(checked.matches.length)} UIDs`}
                  </button>
                </div>
              </div>
            )}
          </section>
        </>
      )}

      <UploadUIDsModal
        isOpen={uploadOpen}
        onClose={closeUpload}
        eventId={eventId}
        eventName={eventName}
      />
    </div>
  );
};
